import React from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {GoogleService} from '../api';
import {ReverseGeocode} from '../api/types';
import {getUserLocation, setUserLocationPlace} from '../store';
import {Places} from '../utils';
import useApiClient, {isThrottled} from './useApiClient';

/**
 * How long to wait before asking again about a point that got no name, after
 * the 1st, 2nd, 3rd and 4th lookup. Five lookups at most per point and locale
 * in a session (FE #387): open ocean really has no name, and every new point
 * is a billed Google call on the backend.
 */
const RETRY_DELAYS_MS = [1, 5, 15, 60].map((minutes) => minutes * 60_000);

/**
 * The least a 429 waits. DRF's `Retry-After` never exceeds the window of the
 * rate that refused, and the reverse-geocode throttle (backend#288) counts
 * per minute and per hour, so an hour honours it without reading the header.
 * The generic `throttled` handling belongs to the api client (FE #404).
 */
const THROTTLED_RETRY_FLOOR_MS = 60 * 60_000;

/**
 * The longest any wait can legitimately be. A wait beyond it means the clock
 * went back since it was set: it is cut down to this rather than trusted (a
 * browser fires a timer of more than 2^31 - 1 ms at once, so an unclamped
 * one would spin).
 */
const MAX_WAIT_MS = Math.max(...RETRY_DELAYS_MS, THROTTLED_RETRY_FLOOR_MS);

type LookupHistory = {failures: number; retryAt: number};

/**
 * Per point and locale, for the session: how many lookups found no name, and
 * when the next one may go. Module-level, so a remount of the app bar or a
 * webapp route change does not reset the budget.
 */
const histories = new Map<string, LookupHistory>();

/** The lookup out for a point and locale, shared by every mounted caller. */
const inFlight = new Map<string, Promise<ReverseGeocode | null>>();

/**
 * Until when the endpoint refused us (429). The throttle counts per user or
 * client IP, not per point: another point or language must wait too.
 */
let throttledUntil = 0;

/** Forget every lookup (tests). */
export const clearPlaceLookups = () => {
  histories.clear();
  inFlight.clear();
  throttledUntil = 0;
};

const hasName = (place: ReverseGeocode | null | undefined): boolean =>
  Places.hasPlaceLabel(Places.buildPlaceLabel(place));

const recordFailure = (key: string): null => {
  const failures = (histories.get(key)?.failures ?? 0) + 1;
  const delay = RETRY_DELAYS_MS[failures - 1];
  histories.set(key, {
    failures,
    retryAt: delay === undefined ? Infinity : Date.now() + delay,
  });
  return null;
};

/**
 * How long until a point may be looked up: 0 when due, Infinity when its
 * lookups are spent for the session.
 */
const waitFor = (key: string): number => {
  const now = Date.now();
  const history = histories.get(key);
  let wait = Math.max(history?.retryAt ?? 0, throttledUntil) - now;
  if (wait > MAX_WAIT_MS && wait !== Infinity) {
    // The clock went back: wait the longest legitimate wait from now.
    if (history && history.retryAt - now > MAX_WAIT_MS) {
      history.retryAt = now + MAX_WAIT_MS;
    }
    throttledUntil = Math.min(throttledUntil, now + MAX_WAIT_MS);
    wait = MAX_WAIT_MS;
  }
  return Math.max(wait, 0);
};

/** The place, or null when the lookup found no name or failed. */
const lookUp = (
  key: string,
  request: () => Promise<ReverseGeocode | null | undefined>,
): Promise<ReverseGeocode | null> => {
  const pending = inFlight.get(key);
  if (pending) {
    return pending;
  }
  const lookup: Promise<ReverseGeocode | null> = request()
    .then((place) => {
      if (place && hasName(place)) {
        histories.delete(key);
        return place;
      }
      return recordFailure(key);
    })
    .catch((error: unknown) => {
      if (isThrottled(error)) {
        throttledUntil = Date.now() + THROTTLED_RETRY_FLOOR_MS;
      }
      return recordFailure(key);
    })
    .finally(() => {
      // Only its own entry: one left over from before `clearPlaceLookups`
      // must not drop the lookup that replaced it.
      if (inFlight.get(key) === lookup) {
        inFlight.delete(key);
      }
    });
  inFlight.set(key, lookup);
  return lookup;
};

/**
 * Keep the GPS entry's place name in step with its coordinates and the locale.
 * Both apps mount it where the GPS entry is shown (FE #381).
 *
 * Three things leave the name to look up, and only one of them is movement:
 *
 * - **The coordinates changed.** A fix at a new point arrives without
 *   `place` (core `setUserLocation` keeps it only for the same point).
 * - **The language changed.** Google localises place names — Munich is
 *   "München" in German and "Мюнхен" in Russian — so a label resolved before
 *   a language switch is stale even though the device has not moved.
 *   `placeLocale` records what it was resolved in and is compared against the
 *   active locale.
 * - **There is no name yet.** An answer whose label renders empty is not a
 *   name (FE #387): the backend answers three empty strings for "nothing
 *   here" (open ocean, a coastal grid point offshore: backend#275) and, until
 *   backend#275 ships, for an outage or a quota error too. It is never
 *   stored, and a place stored empty by an older build counts as unnamed.
 *
 * An empty answer and a failed request (offline, timeout, a non-2xx, a 429)
 * are asked again after a backoff, five lookups at most per point and locale
 * in the session: see `RETRY_DELAYS_MS`. The retry comes from a timer while
 * the hook is mounted, and from the app coming back: `visibilitychange` and
 * `online` in the webapp, listened to here; React Native has no `document`,
 * so mobile calls the returned wake from its AppState foreground hook.
 *
 * Failures are deliberately silent, which takes `{silent: true}` and not just
 * a `.catch`: `useApiClient` dispatches the global error alert itself before
 * it rejects, so swallowing only this promise would still pop an error
 * snackbar on every offline launch for a lookup the user never asked for.
 * Until a name arrives the app bar falls back to "Current location". There is
 * nothing to tell the user and nothing they could do about it.
 *
 * @returns A wake: asks again now if a retry is due. A no-op otherwise (no
 * request, no render), so a foreground signal may call it freely.
 */
const useUserLocationPlace = (): (() => void) => {
  const dispatch = useDispatch();
  const {fetch} = useApiClient();
  const {locale} = useIntl();
  const userLocation = useSelector(getUserLocation);

  const latitude = userLocation?.coordinates.latitude;
  const longitude = userLocation?.coordinates.longitude;
  const isStale =
    Boolean(userLocation) &&
    (!hasName(userLocation?.place) || userLocation?.placeLocale !== locale);

  // Bumped to re-run the lookup effect.
  const [runs, setRuns] = React.useState(0);
  const rerun = React.useCallback(() => setRuns((n) => n + 1), []);
  // When the retry the effect waits for is due; Infinity while it waits for
  // none. A wake before then does nothing, so a foreground with nothing to
  // look up does not re-render the host screen.
  const dueAtRef = React.useRef(Infinity);
  const wake = React.useCallback(() => {
    if (Date.now() >= dueAtRef.current) {
      rerun();
    }
  }, [rerun]);

  React.useEffect(() => {
    dueAtRef.current = Infinity;
    if (!isStale || latitude === undefined || longitude === undefined) {
      return undefined;
    }

    const key = `${latitude},${longitude},${locale}`;
    const wait = waitFor(key);
    if (wait === Infinity) {
      // Out of lookups for this point in this session.
      return undefined;
    }
    if (wait > 0) {
      dueAtRef.current = Date.now() + wait;
      const timer = setTimeout(rerun, wait);
      return () => {
        clearTimeout(timer);
      };
    }

    let cancelled = false;

    lookUp(key, () =>
      fetch(GoogleService.reverseGeocode(latitude, longitude), {silent: true}),
    )
      .then((place) => {
        if (cancelled) {
          return;
        }
        if (!place) {
          // Re-run to wait for the retry the failure scheduled.
          rerun();
          return;
        }
        const label = Places.buildPlaceLabel(place);
        dispatch(
          // A merge keyed on the coordinates, not a replace with the snapshot
          // this closure captured. The reducer drops the write if the location
          // has moved or been cleared in the meantime — otherwise a resolve
          // landing just after the user revokes the permission would put them
          // back on the location they denied.
          setUserLocationPlace({
            coordinates: {latitude, longitude},
            place,
            placeLocale: locale,
            // `title` carries the full composed label so the surfaces that
            // only read a plain string — the forecast-model sheet among them —
            // say the same words as the app bar.
            title: Places.formatPlaceTitle(label),
            subtitle: label.subtitle || undefined,
          }),
        );
      })
      .catch(() => {
        // Intentionally silent — see the doc comment.
      });

    return () => {
      cancelled = true;
    };
  }, [isStale, latitude, longitude, locale, runs, dispatch, fetch, rerun]);

  // The webapp's way back: a hidden tab's timers are throttled, a suspended
  // one's stopped. Reached through globalThis because the mobile tsconfig has
  // no DOM lib; React Native has no `document`, so this is a no-op there.
  React.useEffect(() => {
    const view = globalThis as unknown as {
      document?: {
        visibilityState?: string;
        addEventListener?: (type: string, listener: () => void) => void;
        removeEventListener?: (type: string, listener: () => void) => void;
      };
      addEventListener?: (type: string, listener: () => void) => void;
      removeEventListener?: (type: string, listener: () => void) => void;
    };
    const doc = view.document;
    if (!doc?.addEventListener) {
      return undefined;
    }
    const onWake = () => {
      if (doc.visibilityState !== 'hidden') {
        wake();
      }
    };
    doc.addEventListener('visibilitychange', onWake);
    view.addEventListener?.('online', onWake);
    return () => {
      doc.removeEventListener?.('visibilitychange', onWake);
      view.removeEventListener?.('online', onWake);
    };
  }, [wake]);

  return wake;
};

export default useUserLocationPlace;
